<template>
  <vs-popup title="Panduan TVD Support Tools" :active.sync="isActive" :class="'panduan-tvd-popup'">
    <div class="panduan-container">
      <div class="panduan-body">
        <!-- Sidebar Navigation -->
        <div class="panduan-sidebar" ref="panduanSidebar">
          <div
            v-for="(tool, index) in tools"
            :key="index"
            :ref="'panduanNav' + index"
            class="panduan-nav-item"
            :class="{ 'panduan-nav-active': activeTool === index }"
            @click="selectTool(index)"
          >
            <div class="panduan-nav-icon" :style="{ background: tool.color + '15', color: tool.color }">
              <i :class="tool.icon"></i>
            </div>
            <div class="panduan-nav-label">{{ tool.name }}</div>
          </div>
        </div>

        <!-- Content Area -->
        <div class="panduan-content">
          <div v-for="(tool, index) in tools" :key="'content-' + index" v-show="activeTool === index">
            <!-- Tool Header -->
            <div class="panduan-tool-header">
              <div class="panduan-tool-icon-lg" :style="{ background: tool.color, color: '#fff' }">
                <i :class="tool.icon"></i>
              </div>
              <div>
                <h3 style="margin:0;font-size:16px;font-weight:700;color:#1a1a2e;">{{ tool.name }}</h3>
                <p style="margin:4px 0 0;font-size:13px;color:#555;">{{ tool.description }}</p>
              </div>
            </div>

            <!-- Use Case -->
            <div class="panduan-section">
              <div class="panduan-section-title">
                <i class="feather icon-help-circle" style="color:#6c63ff;"></i>
                <span>Kapan Digunakan?</span>
              </div>
              <div class="panduan-usecase">
                <p v-for="(uc, ui) in tool.useCases" :key="ui" style="margin:4px 0;font-size:13px;color:#444;">
                  {{ uc }}
                </p>
              </div>
            </div>

            <!-- Steps -->
            <div class="panduan-section">
              <div class="panduan-section-title">
                <i class="feather icon-list" style="color:#00b894;"></i>
                <span>Langkah-langkah</span>
              </div>
              <div class="panduan-steps">
                <div v-for="(step, si) in tool.steps" :key="si" class="panduan-step">
                  <div class="panduan-step-number" :style="{ background: tool.color }">{{ si + 1 }}</div>
                  <div class="panduan-step-body">
                    <div class="panduan-step-title">{{ step.title }}</div>
                    <div class="panduan-step-desc" v-html="step.desc"></div>
                    <!-- Field table if exists -->
                    <div v-if="step.fields" class="panduan-fields-table">
                      <table>
                        <thead>
                          <tr>
                            <th>Field</th>
                            <th>Keterangan</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="(field, fi) in step.fields" :key="fi">
                            <td><code>{{ field.name }}</code></td>
                            <td>{{ field.desc }}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Tips -->
            <div class="panduan-section">
              <div class="panduan-section-title">
                <i class="feather icon-alert-triangle" style="color:#fdcb6e;"></i>
                <span>Tips & Catatan Penting</span>
              </div>
              <div class="panduan-tips">
                <div v-for="(tip, ti) in tool.tips" :key="ti" class="panduan-tip-item">
                  <div class="panduan-tip-icon" :class="tip.type">
                    <i :class="tip.type === 'warning' ? 'feather icon-alert-circle' : tip.type === 'info' ? 'feather icon-info' : 'feather icon-check-circle'"></i>
                  </div>
                  <div class="panduan-tip-text">{{ tip.text }}</div>
                </div>
              </div>
            </div>

            <!-- Result Info -->
            <div class="panduan-section" v-if="tool.results && tool.results.length">
              <div class="panduan-section-title">
                <i class="feather icon-bar-chart-2" style="color:#6c63ff;"></i>
                <span>Output / Hasil yang Ditampilkan</span>
              </div>
              <div class="panduan-results">
                <div v-for="(result, ri) in tool.results" :key="ri" class="panduan-result-chip" :style="{ borderColor: tool.color + '40', background: tool.color + '08' }">
                  <span style="font-weight:600;color:#333;">{{ result.label }}</span>
                  <span style="font-size:12px;color:#666;">{{ result.desc }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </vs-popup>
</template>

<script>
export default {
  name: "PanduanTVD",
  props: {
    active: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isActive: false,
      activeTool: 0,
      tools: [
        {
          name: "Change Inbound Reference",
          icon: "feather icon-edit-2",
          color: "#6c63ff",
          description: "Mengubah reference (nomor referensi) dari data Inbound yang sudah ada di sistem.",
          useCases: [
            "Ketika nomor referensi inbound salah input dan perlu dikoreksi.",
            "Ketika ada perubahan nomor referensi dari pihak supplier/client.",
            "Ketika data inbound perlu disesuaikan dengan dokumen asli.",
          ],
          steps: [
            {
              title: "Masukkan Current Inbound Reference",
              desc: 'Di kolom <b>Current Inbound Reference</b>, masukkan nomor referensi inbound yang ingin diubah. Lalu klik tombol <b>Search</b> atau tekan Enter.',
              fields: [
                { name: "Current Inbound Reference", desc: "Nomor referensi inbound yang sedang berlaku (lama)." },
              ],
            },
            {
              title: "Review Preview Data",
              desc: "Setelah pencarian berhasil, akan muncul card <b>Inbound Info</b> berisi informasi supplier, client, warehouse, dan status inbound. Pastikan data sudah benar sebelum melanjutkan.",
            },
            {
              title: "Masukkan New Inbound Reference",
              desc: 'Di kolom <b>New Inbound Reference</b>, masukkan nomor referensi inbound yang baru.',
              fields: [
                { name: "New Inbound Reference", desc: "Nomor referensi inbound baru yang diinginkan." },
              ],
            },
            {
              title: "Klik Process & Konfirmasi",
              desc: 'Klik tombol <b>Process</b>. Muncul popup konfirmasi — klik <b>Ya, Update</b> untuk menjalankan update. Sistem akan memperbarui referensi di tabel inbound dan inventories terkait.',
            },
          ],
          tips: [
            { type: "warning", text: "Pastikan Old dan New Reference berbeda. Jika sama, sistem akan menolak." },
            { type: "warning", text: "Data yang diupdate meliputi tabel inbound dan semua inventories transactions terkait." },
            { type: "success", text: "Setelah update, notifikasi sukses akan menampilkan perubahan reference secara eksplisit." },
          ],
          results: [
            { label: "Inbound Info", desc: "Supplier, client, warehouse, status" },
            { label: "Reference Update", desc: "Old → New reference pada record inbound" },
          ],
        },
        {
          name: "Change GR Reference",
          icon: "feather icon-refresh-cw",
          color: "#00b894",
          description: "Mengubah reference dari data Good Receive (GR) beserta transaksi inventories dan movement order terkait.",
          useCases: [
            "Ketika nomor referensi GR salah input.",
            "Ketika ada perubahan nomor dokumen GR dari SAP atau pihak terkait.",
            "GR perlu disesuaikan dengan dokumen asli dari supplier.",
          ],
          steps: [
            {
              title: "Masukkan Current GR Reference",
              desc: 'Di kolom <b>Current GR Reference</b>, masukkan nomor referensi GR yang ingin diubah. Klik <b>Search</b> atau tekan Enter.',
              fields: [
                { name: "Current GR Reference", desc: "Nomor referensi Good Receive yang sedang berlaku." },
              ],
            },
            {
              title: "Review GR Details",
              desc: "Preview akan menampilkan <b>GR Details</b> (daftar produk dan qty) serta <b>Records Akan Diupdate</b> berupa jumlah record GR, Inventories Transactions, dan Movement Order Details yang akan terdampak.",
            },
            {
              title: "Masukkan New GR Reference",
              desc: 'Di kolom <b>New GR Reference</b>, masukkan nomor referensi GR baru.',
              fields: [
                { name: "New GR Reference", desc: "Nomor referensi Good Receive baru." },
              ],
            },
            {
              title: "Klik Process & Konfirmasi",
              desc: 'Klik tombol <b>Process</b>, lalu konfirmasi di popup dengan klik <b>Ya, Update</b>. Sistem akan memperbarui referensi di tabel GR, inventories transactions, dan movement order details.',
            },
          ],
          tips: [
            { type: "warning", text: "Update GR Reference berdampak lebih luas dari Inbound Reference — meliputi GR record, inventories transactions, DAN movement order details." },
            { type: "warning", text: "Pastikan Old dan New Reference berbeda." },
            { type: "info", text: "Jumlah record yang akan diupdate ditampilkan di preview, sehingga kamu bisa review sebelum eksekusi." },
          ],
          results: [
            { label: "GR Details", desc: "Daftar produk & qty dalam GR" },
            { label: "Records Akan Diupdate", desc: "Jumlah record GR, transactions, movements" },
          ],
        },
        {
          name: "Change Serial Number",
          icon: "feather icon-hash",
          color: "#e17055",
          description: "Mengubah serial number (Pack ID) dari item inbound secara batch menggunakan file Excel.",
          useCases: [
            "Ketika serial number / pack ID salah input untuk banyak item sekaligus.",
            "Ketika ada koreksi massal dari pihak gudang atau client.",
            "Perubahan pack ID untuk item yang sudah terlanjur di-GR.",
          ],
          steps: [
            {
              title: "Download Template Excel",
              desc: 'Klik tombol <b>Download</b> di bawah label <b>Template</b>. File <code>template_change_pack_id.xlsx</code> akan terdownload. Buka file tersebut.',
            },
            {
              title: "Isi Template dengan Data",
              desc: "Isi kolom dalam template: <b>old serial number</b> dan <b>new serial number</b>. Pastikan format sesuai dengan petunjuk di template.",
            },
            {
              title: "Upload File Excel",
              desc: 'Klik area <b>File Excel</b> dan pilih file yang sudah diisi. Format yang diterima: <code>.xlsx</code> atau <code>.xls</code>.',
              fields: [
                { name: "File Excel", desc: "File berisi mapping pack ID lama → baru (format .xlsx/.xls)." },
              ],
            },
            {
              title: "Klik Process & Konfirmasi",
              desc: 'Klik tombol <b>Process</b>. Konfirmasi dialog akan muncul — klik <b>Ya, Process</b> untuk menjalankan update.',
            },
            {
              title: "Lihat Hasil Update",
              desc: "Hasil akan ditampilkan dalam tabel berisi Old Pack ID → New Pack ID, beserta jumlah record yang diupdate (GR Item Checks, Movement, Inventories, Transactions).",
            },
          ],
          tips: [
            { type: "warning", text: "Hanya file .xlsx dan .xls yang diterima. Format lain akan ditolak." },
            { type: "warning", text: "Pastikan isi template benar sebelum upload — data yang sudah diproses sulit dirollback." },
            { type: "success", text: "Hasil update ditampilkan secara detail: GR checks, movement, inventories, dan transactions yang terdampak." },
          ],
          results: [
            { label: "OLD PACK ID → NEW PACK ID", desc: "Tabel mapping perubahan serial" },
            { label: "GR Checks Updated", desc: "Jumlah record GR item checks" },
            { label: "Movement Updated", desc: "Jumlah record movement order" },
            { label: "Inventories Updated", desc: "Jumlah record inventories" },
            { label: "Transactions Updated", desc: "Jumlah record inventory transactions" },
          ],
        },
        {
          name: "Change Serial Number on GI/TWH",
          icon: "feather icon-grid",
          color: "#e17055",
          description: "Mengubah serial number pada Good Issue atau Transfer Warehouse berdasarkan nomor DO melalui file Excel.",
          useCases: [
            "Ketika serial number pada Good Issue atau Transfer Warehouse perlu dikoreksi.",
            "Ketika serial number salah input pada DO yang sudah diproses.",
          ],
          steps: [
            {
              title: "Masukkan DO Number",
              desc: "Masukkan nomor Delivery Order yang serial number-nya akan diubah.",
              fields: [
                { name: "DO Number", desc: "Nomor referensi Delivery Order yang akan diproses." },
              ],
            },
            {
              title: "Download dan Isi Template Excel",
              desc: "Klik <b>Download</b>, lalu isi kolom <b>old serial number</b> dan <b>new serial number</b> pada template.",
            },
            {
              title: "Upload File Excel",
              desc: "Pilih file template yang sudah diisi. Format yang diterima: <code>.xlsx</code> atau <code>.xls</code>.",
              fields: [
                { name: "File Excel", desc: "File mapping old serial number ke new serial number." },
              ],
            },
            {
              title: "Klik Process dan Konfirmasi",
              desc: "Klik <b>Process</b>, lalu pilih <b>Ya, Process</b> pada dialog konfirmasi untuk menjalankan perubahan.",
            },
          ],
          tips: [
            { type: "warning", text: "Pastikan DO Number dan mapping serial number sudah benar. Perubahan diproses dalam satu transaksi." },
            { type: "warning", text: "Jika satu serial number tidak ditemukan untuk DO tersebut, seluruh perubahan dibatalkan." },
          ],
          results: [
            { label: "Rows Processed", desc: "Jumlah baris Excel yang diproses" },
            { label: "DO Details Updated", desc: "Jumlah detail DO yang diubah" },
            { label: "Transactions Updated", desc: "Jumlah inventory transaction yang diubah" },
            { label: "Inventories Updated", desc: "Jumlah inventory yang diubah" },
          ],
        },
        {
          name: "Change Warehouse",
          icon: "feather icon-home",
          color: "#0984e3",
          description: "Mengubah warehouse dari item inbound secara batch menggunakan file Excel. Mendukung perubahan warehouse untuk TV dan Bracket.",
          useCases: [
            "Ketika item inbound salah diletakkan di warehouse yang salah.",
            "Ketika ada perpindahan barang antar warehouse yang perlu dicatat di sistem.",
            "Koreksi warehouse untuk data inbound yang sudah terlanjur diproses.",
          ],
          steps: [
            {
              title: "Download Template Excel",
              desc: 'Klik tombol <b>Download</b> di bawah label <b>Template</b>. File <code>template_change_warehouse.xlsx</code> akan terdownload.',
            },
            {
              title: "Isi Template dengan Data",
              desc: "Isi kolom dalam template: <b>reference</b> dan <b>warehouse</b>. Pastikan format sesuai dengan petunjuk di template.",
            },
            {
              title: "Upload File Excel",
              desc: 'Klik area <b>File Excel</b> dan pilih file yang sudah diisi. Format yang diterima: <code>.xlsx</code> atau <code>.xls</code>.',
              fields: [
                { name: "File Excel", desc: "File berisi mapping warehouse lama → baru (format .xlsx/.xls)." },
              ],
            },
            {
              title: "Klik Process & Konfirmasi",
              desc: 'Klik tombol <b>Process</b>. Konfirmasi dialog akan muncul — klik <b>Ya, Process</b> untuk menjalankan update.',
            },
            {
              title: "Lihat Hasil Update",
              desc: "Hasil akan ditampilkan secara detail dalam tabel: Reference, GR Reference, Old Warehouse ID → New Warehouse ID, beserta jumlah record yang diupdate (Inbounds, GR, Movement, Transactions, Inventories, dll).",
            },
          ],
          tips: [
            { type: "warning", text: "Change Warehouse berdampak pada banyak tabel sekaligus: inbounds, GR, movement, transactions, inventories TV, dan bracket (incremented/decremented)." },
            { type: "warning", text: "Pastikan isi template benar — perubahan warehouse akan mempengaruhi stok di kedua warehouse (source & destination)." },
          ],
          results: [
            { label: "Rows Processed", desc: "Jumlah baris yang diproses dari Excel" },
            { label: "Inbounds Updated", desc: "Jumlah record inbound" },
            { label: "GR Updated", desc: "Jumlah record Good Receive" },
            { label: "Movement Updated", desc: "Jumlah record movement order" },
            { label: "Transactions Updated", desc: "Jumlah inventory transactions" },
            { label: "TV Inventories Updated", desc: "Jumlah inventories TV" },
            { label: "Bracket Decremented / Incremented", desc: "Perubahan stok bracket" },
          ],
        },
        {
          name: "Change Delivery Order's Outbound",
          icon: "feather icon-truck",
          color: "#d63031",
          description: "Memindahkan Delivery Order (DO) dari satu outbound ke outbound lainnya, termasuk transport orders dan detail terkait.",
          useCases: [
            "Ketika DO salah ditambahkan ke outbound yang salah.",
            "Ketika DO perlu dipindahkan ke outbound lain sebelum Good Issue ataupun setelah Good Issued.",
            "Reorganisasi DO antar outbound untuk efisiensi pengiriman.",
          ],
          steps: [
            {
              title: "Masukkan Current DO Reference",
              desc: 'Di kolom <b>Current DO Reference</b>, masukkan nomor referensi DO yang ingin dipindahkan.',
              fields: [
                { name: "Current DO Reference", desc: "Nomor referensi Delivery Order yang ingin dipindah." },
              ],
            },
            {
              title: "Masukkan New Outbound Reference",
              desc: 'Di kolom <b>New Outbound Reference</b>, masukkan nomor referensi outbound tujuan. Klik <b>Process</b> atau tekan Enter.',
              fields: [
                { name: "New Outbound Reference", desc: "Nomor referensi outbound tujuan baru." },
              ],
            },
            {
              title: "Konfirmasi Update",
              desc: 'Popup konfirmasi akan menampilkan DO Reference (Old) dan Outbound Reference (New). Klik <b>Ya, Update</b> untuk menjalankan pemindahan.',
            },
          ],
          tips: [
            { type: "warning", text: "DO Reference dan Outbound Reference keduanya wajib diisi. Sistem tidak akan memproses jika salah satu kosong." },
            { type: "warning", text: "Pastikan outbound tujuan sudah ada di sistem. Jika tidak, pemindahan akan gagal." },
            { type: "success", text: "Notifikasi sukses akan menampilkan konfirmasi bahwa outbound reference berhasil diperbarui." },
          ],
          results: [
            { label: "Outbound Reference", desc: "DO dipindah dari outbound lama → baru" },
            { label: "Transport Orders", desc: "Transport order ikut dipindah" },
            { label: "Committed Qty", desc: "Qty terkait direconcile" },
          ],
        },
        {
          name: "Change Outbound Reference",
          icon: "feather icon-repeat",
          color: "#2d98da",
          description: "Mengubah outbound reference pada data outbound dan AWB terkait.",
          useCases: [
            "Ketika nomor outbound salah input.",
            "Ketika outbound reference perlu disamakan dengan dokumen sumber.",
            "Ketika AWB dan internal reference harus ikut tersinkron.",
          ],
          steps: [
            {
              title: "Masukkan Current Outbound Reference",
              desc: 'Di kolom <b>Current Outbound Reference</b>, masukkan reference outbound yang ingin diubah. Klik <b>Search</b> atau tekan Enter.',
              fields: [
                { name: "Current Outbound Reference", desc: "Reference outbound yang sedang aktif." },
              ],
            },
            {
              title: "Review Preview Data",
              desc: "Preview akan menampilkan info outbound dan jumlah record yang akan diupdate. Pastikan data sudah benar sebelum lanjut.",
            },
            {
              title: "Masukkan New Outbound Reference",
              desc: 'Di kolom <b>New Outbound Reference</b>, masukkan reference outbound baru.',
              fields: [
                { name: "New Outbound Reference", desc: "Reference outbound baru yang diinginkan." },
              ],
            },
            {
              title: "Klik Process & Konfirmasi",
              desc: 'Klik tombol <b>Process</b>, lalu konfirmasi di popup dengan klik <b>Ya, Update</b>. Sistem akan memperbarui outbound reference dan AWB terkait.',
            },
          ],
          tips: [
            { type: "warning", text: "Pastikan old dan new reference berbeda." },
            { type: "warning", text: "Perubahan juga menyentuh AWB internal reference dan tracking reference." },
            { type: "success", text: "Hasil sukses menampilkan old reference dan new reference secara eksplisit." },
          ],
          results: [
            { label: "Outbound Info", desc: "Outbound reference, status, client, origin, destination" },
            { label: "AWB", desc: "Internal reference dan tracking reference ikut diupdate" },
          ],
        },
        {
          name: "Change DO Reference",
          icon: "feather icon-truck",
          color: "#9b59b6",
          description: "Mengubah DO reference dan record terkait yang memakai referensi DO tersebut.",
          useCases: [
            "Ketika nomor DO salah input.",
            "Ketika DO reference perlu disesuaikan setelah perubahan dokumen.",
            "Ketika transport order dan transaksi masih memakai reference lama.",
          ],
          steps: [
            {
              title: "Masukkan Current DO Reference",
              desc: 'Di kolom <b>Current DO Reference</b>, masukkan nomor DO yang ingin diubah. Klik <b>Search</b> atau tekan Enter.',
              fields: [
                { name: "Current DO Reference", desc: "Nomor DO yang sedang aktif." },
              ],
            },
            {
              title: "Review Preview Data",
              desc: "Preview akan menampilkan info DO dan jumlah record yang terdampak: delivery order, transport orders, inventory transactions, dan AWB.",
            },
            {
              title: "Masukkan New DO Reference",
              desc: 'Di kolom <b>New DO Reference</b>, masukkan nomor DO baru.',
              fields: [
                { name: "New DO Reference", desc: "Nomor DO baru yang diinginkan." },
              ],
            },
            {
              title: "Klik Process & Konfirmasi",
              desc: 'Klik tombol <b>Process</b>, lalu konfirmasi di popup dengan klik <b>Ya, Update</b>. Sistem akan memperbarui DO reference dan record terkait.',
            },
          ],
          tips: [
            { type: "warning", text: "Pastikan old dan new DO reference berbeda." },
            { type: "warning", text: "Perubahan menjangkau delivery_orders, transport_orders, inventories_transactions, dan AWB." },
            { type: "success", text: "Preview menampilkan data DO sebelum update dijalankan." },
          ],
          results: [
            { label: "DO Info", desc: "Reference, status, qty ringkas" },
            { label: "Records Akan Diupdate", desc: "Delivery order, transport order, transaksi, AWB" },
          ],
        },
        {
          name: "Change Inbound Notes",
          icon: "feather icon-file-text",
          color: "#636e72",
          description: "Mengubah notes pada data Inbound secara batch menggunakan file Excel.",
          useCases: [
            "Ketika notes inbound salah input dan perlu dikoreksi secara massal.",
            "Ketika ada penambahan atau perubahan catatan dari pihak gudang atau client.",
            "Ketika data inbound perlu dilengkapi informasi catatan tambahan.",
          ],
          steps: [
            {
              title: "Download Template Excel",
              desc: 'Klik tombol <b>Download</b> di bawah label <b>Template</b>. File <code>template_change_inbound_notes.xlsx</code> akan terdownload.',
            },
            {
              title: "Isi Template dengan Data",
              desc: "Isi kolom dalam template: <b>reference</b> dan <b>note</b>. Pastikan nama kolom dan format sesuai dengan template.",
            },
            {
              title: "Upload File Excel",
              desc: 'Klik area <b>File Excel</b> dan pilih file yang sudah diisi. Format yang diterima: <code>.xlsx</code> atau <code>.xls</code>.',
              fields: [
                { name: "File Excel", desc: "File berisi kolom reference dan note (format .xlsx/.xls)." },
              ],
            },
            {
              title: "Klik Process & Konfirmasi",
              desc: 'Klik tombol <b>Process</b>. Konfirmasi dialog akan muncul — klik <b>Ya, Process</b> untuk menjalankan update.',
            },
          ],
          tips: [
            { type: "warning", text: "Pastikan isi template benar sebelum upload — data yang sudah diproses sulit dirollback." },
            { type: "info", text: "Format yang diterima hanya .xlsx dan .xls." },
          ],
          results: [
            { label: "Reference", desc: "Nomor referensi inbound yang diupdate" },
            { label: "New Inbound Notes", desc: "Catatan baru yang diterapkan" },
            { label: "Rows Processed", desc: "Jumlah baris yang diproses" },
            { label: "Inbounds Updated", desc: "Jumlah record inbound yang diupdate" },
          ],
        },
        {
          name: "Sync Bracket Inventory",
          icon: "feather icon-refresh-cw",
          color: "#f39c12",
          description: "Sinkronisasi stok bracket inventory antara perhitungan sistem (Beginning + Outbound = End Stock) dengan stok aktual di WMS (Stock WMS).",
          useCases: [
            "Ketika stok bracket di WMS tidak sesuai dengan perhitungan Beginning Stock ± Outbound.",
            "Ketika ada selisih (GAP) antara End Stock dan Stock WMS yang perlu dikoreksi.",
            "Ketika stock opname atau audit menemukan bracket inventory tidak akurat.",
          ],
          steps: [
            {
              title: "Review Data Inventory",
              desc: "Halaman otomatis memuat data inventory dari <b>GET /support/adjust-inventory-preview</b>. Tabel menampilkan: <b>Warehouse</b>, <b>Product ID</b>, <b>Beginning Stock</b>, <b>Outbound</b>, <b>End Stock</b>, <b>Stock WMS</b>, dan <b>GAP</b>.",
            },
            {
              title: "Filter Warehouse (Opsional)",
              desc: 'Di kolom <b>Filter Warehouse</b>, ketik nama warehouse untuk memfilter data. Pagination otomatis reset ke halaman pertama.',
              fields: [
                { name: "Filter Warehouse", desc: "Ketik nama warehouse untuk filter (case-insensitive)." },
              ],
            },
            {
              title: "Identifikasi GAP",
              desc: "Baris yang memiliki selisih (GAP ≠ 0) ditampilkan dengan <b>latar merah muda</b> dan angka berwarna <b>merah tebal</b>. Kolom GAP menunjukkan perbedaan antara Stock WMS dan End Stock.",
            },
            {
              title: "Klik Sync Qty",
              desc: 'Pada baris yang ingin disinkronisasi, klik tombol <b>Sync Qty</b> (kuning). Muncul popup detail menampilkan Warehouse, Product, Current Base Qty, End Stock, dan GAP.',
            },
            {
              title: "Konfirmasi Sync",
              desc: 'Klik tombol <b>Sync</b> di popup detail. Muncul popup konfirmasi — klik <b>Ya, Sync</b> untuk menjalankan sinkronisasi.',
            },
          ],
          tips: [
            { type: "warning", text: "Sync hanya mengupdate stok bracket per warehouse — pastikan warehouse yang dipilih sudah benar." },
            { type: "warning", text: "GAP menunjukkan selisih: jika GAP positif, WMS kelebihan stok; jika negatif, WMS kekurangan." },
            { type: "info", text: "Data otomatis dimuat saat halaman dibuka — tidak perlu input manual." },
            { type: "success", text: "Tabel otomatis refresh setelah sync berhasil sehingga data terbaru langsung terlihat." },
          ],
          results: [
            { label: "Warehouse", desc: "Nama warehouse yang disinkronisasi" },
            { label: "Product ID", desc: "Kode product bracket" },
            { label: "Current Base Qty → End Stock", desc: "Stok WMS disesuaikan menjadi End Stock" },
            { label: "GAP", desc: "Selisih yang di-resolve menjadi 0" },
          ],
        },
      {
        name: "Cancel Good Issue Based on DO",
        icon: "feather icon-x-circle",
        color: "#e84393",
        description: "Membatalkan Good Issue (GI) pada Delivery Order (DO) yang sudah di-GI, menghapus DO dan mengembalikan stok ke warehouse asal (origin).",
        useCases: [
          "Ketika ada DO yang terlanjur di-Good Issue tapi harus dibatalkan.",
          "Ketika pembatalan seluruh DO — tidak per item.",
          "Ketika stok perlu dikembalikan ke warehouse asal setelah GI dibatalkan.",
        ],
        steps: [
          {
            title: "Masukkan DO Reference",
            desc: 'Di kolom <b>DO Reference</b>, masukkan nomor referensi DO yang ingin dibatalkan. Klik <b>Search</b> atau tekan Enter.',
            fields: [
              { name: "DO Reference", desc: "Nomor referensi Delivery Order yang akan di-cancel." },
            ],
          },
          {
            title: "Review Preview Data",
            desc: "Preview akan menampilkan <b>DO Info</b> (reference, status, TV qty, Bracket qty, Bracket base qty), serta <b>Records Akan Diupdate</b> (jumlah inventories transactions, DO details, DO).",
          },
          {
            title: "Klik Process & Konfirmasi",
            desc: 'Klik tombol <b>Process</b>. Muncul popup konfirmasi berisi ringkasan DO, TV qty, dan Bracket qty. Klik <b>Ya, Cancel GI</b> untuk menjalankan pembatalan.',
          },
          {
            title: "Lihat Hasil Cancel GI",
            desc: "Hasil akan menampilkan DO Reference, jumlah inventories yang dibuat, serta jumlah record yang dihapus/diupdate (outbound details, DO details, transactions, DO).",
          },
        ],
        tips: [
          { type: "warning", text: "Cancel GI Based on DO menghapus DO secara keseluruhan — tidak ada mode per item." },
          { type: "warning", text: "Cancel GI Based on DO memindahkan stok kembali ke warehouse asal (origin) — pastikan DO reference sudah benar sebelum konfirmasi." },
          { type: "success", text: "Preview ditampilkan sebelum submit, sehingga kamu bisa review DO sebelum eksekusi." },
        ],
        results: [
          { label: "DO Info", desc: "Reference, status, TV qty, Bracket qty, Bracket base qty" },
          { label: "Records Akan Diupdate", desc: "Jumlah inventories transactions, DO details, DO" },
          { label: "Hasil Cancel GI", desc: "DO Reference, inventories created, record deleted/updated" },
        ],
      },
      {
        name: "Cancel GI Partial",
        icon: "feather icon-x-circle",
        color: "#e84393",
        description: "Membatalkan Good Issue (GI) secara partial per product pada Delivery Order (DO). Stok dikembalikan ke warehouse asal (origin) sesuai qty yang di-cancel.",
        useCases: [
          "Ketika sebagian item di DO terlanjur di-Good Issue dan perlu dibatalkan.",
          "Ketika cancel per product — bukan seluruh DO.",
          "Ketika stok perlu dikembalikan sebagian ke warehouse asal.",
        ],
        steps: [
          {
            title: "Masukkan DO Reference",
            desc: 'Di kolom <b>DO Reference</b>, masukkan nomor referensi DO. Klik <b>Search</b> atau tekan Enter.',
            fields: [
              { name: "DO Reference", desc: "Nomor referensi Delivery Order yang akan di-cancel partial." },
            ],
          },
          {
            title: "Pilih Item di Preview Table",
            desc: "Tabel menampilkan semua DO details: <b>Product</b>, <b>Warehouse</b>, <b>Qty</b>, <b>Picked Qty</b>, <b>Open Qty</b>. Klik tombol <b>Cancel</b> pada baris item yang ingin dibatalkan.",
          },
          {
            title: "Input Qty to Cancel",
            desc: 'Popup menampilkan info product terpilih. Masukkan <b>Qty to Cancel</b> (harus lebih dari 0 dan tidak boleh melebihi qty item). Klik <b>Process</b>.',
            fields: [
              { name: "Qty to Cancel", desc: "Jumlah quantity yang akan di-cancel (partial)." },
            ],
          },
          {
            title: "Lihat Hasil Cancel GI Partial",
            desc: "Hasil menampilkan <b>Product Code</b>, <b>Cancelled Qty</b>, <b>Inventories Created</b>, <b>Transactions Deleted/Updated</b>, dan <b>Outbound Details Updated</b>. Tabel otomatis refresh dengan data terbaru.",
          },
        ],
        tips: [
          { type: "warning", text: "Tidak bisa cancel GI Partial untuk warehouse central — sistem akan menolak." },
          { type: "warning", text: "Untuk TVD pack (product_id 2), sistem menghapus GI transactions per pack sampai qty terpenuhi." },
          { type: "warning", text: "Untuk Bracket (product_id 1), base_qty transaksi dikurangi — tidak boleh sampai 0." },
          { type: "success", text: "Tabel otomatis refresh setelah cancel berhasil, sehingga qty terbaru langsung terlihat." },
        ],
        results: [
          { label: "Product Code", desc: "Kode product yang di-cancel" },
          { label: "Cancelled Qty", desc: "Jumlah qty yang berhasil di-cancel" },
          { label: "Inventories Created", desc: "Jumlah inventory baru yang dibuat di warehouse asal" },
          { label: "Transactions Deleted/Updated", desc: "Jumlah transaksi GI yang dihapus (TVD) atau diupdate (Bracket)" },
          { label: "Outbound Details Updated", desc: "Jumlah outbound details yang diupdate (open_qty +, committed_qty -)" },
        ],
      },
      {
        name: "Warehouse Stock Transfer",
        icon: "feather icon-shuffle",
        color: "#009688",
        description: "Memindahkan stok TV dan Bracket dari satu warehouse ke warehouse lain.",
        useCases: [
          "Ketika stok TV atau Bracket perlu dipindahkan antar warehouse.",
          "Ketika source warehouse dan destination warehouse sudah ditentukan.",
        ],
        steps: [
          {
            title: "Pilih Warehouse",
            desc: "Pilih <b>Source Warehouse</b> sebagai warehouse asal dan <b>Destination Warehouse</b> sebagai warehouse tujuan.",
            fields: [
              { name: "Source Warehouse", desc: "Kode warehouse asal." },
              { name: "Destination Warehouse", desc: "Kode warehouse tujuan. Harus berbeda dari source warehouse." },
            ],
          },
          {
            title: "Masukkan Quantity",
            desc: "Masukkan quantity TV dan/atau Bracket yang ingin dipindahkan. Minimal salah satu quantity harus lebih dari 0.",
            fields: [
              { name: "TV Qty", desc: "Jumlah stok TV yang dipindahkan." },
              { name: "Bracket Qty", desc: "Jumlah stok Bracket yang dipindahkan." },
            ],
          },
          {
            title: "Konfirmasi Transfer",
            desc: "Klik <b>Process</b>, review ringkasan transfer, lalu klik <b>Ya, Process</b> untuk menjalankan transfer.",
          },
          {
            title: "Lihat Hasil",
            desc: "Jika berhasil, sistem menampilkan pesan <b>Warehouse transfer stock success.</b>. Jika stok sumber tidak cukup, proses dibatalkan dan error ditampilkan.",
          },
        ],
        tips: [
          { type: "warning", text: "Pastikan source dan destination warehouse sudah benar sebelum konfirmasi." },
          { type: "warning", text: "Transfer gagal jika stok source warehouse tidak mencukupi." },
          { type: "info", text: "TV menggunakan product_id 2 dan Bracket menggunakan product_id 1 di backend." },
          { type: "success", text: "Transfer TV dan Bracket diproses dalam satu request transaction." },
        ],
        results: [
          { label: "Transfer Status", desc: "Pesan sukses atau error dari API warehouse transfer." },
          { label: "Source Warehouse", desc: "Warehouse asal transfer." },
          { label: "Destination Warehouse", desc: "Warehouse tujuan transfer." },
        ],
      },
      {
        name: "SN Anomaly Report",
        icon: "feather icon-alert-triangle",
        color: "#d63031",
        description: "Mendeteksi Pack ID (Serial Number) dari transaksi GR TVD yang tidak sesuai dengan pattern SN lazim.",
        useCases: [
          "Ketika ada kecurigaan ada Serial Number yang formatnya tidak lazim / anomali.",
          "Untuk audit rutin kelayakan format Pack ID dari data GR.",
          "Ketika perlu export daftar SN anomali untuk investigasi atau koreksi lebih lanjut.",
        ],
        steps: [
          {
            title: "Buka Tab SN Anomaly Report",
            desc: "Klik menu <b>SN Anomaly Report</b> di sidebar TVD Support Tools. Sistem otomatis memuat data anomali dari transaksi GR dengan <code>product_id = 2</code> (TVD).",
          },
          {
            title: "Review Tabel Anomali",
            desc: "Tabel menampilkan daftar Pack ID yang <b>tidak match</b> dengan pattern SN lazim <code>3TB75B[A-Z0-9]{11}SJ[0-9]{4}</code>. Kolom: Pack ID, Product Code, GR Reference, Base Qty, Created At.",
          },
          {
            title: "Export CSV (Opsional)",
            desc: "Klik tombol <b>Export CSV</b> untuk download daftar anomali sebagai file CSV. File berisi kolom yang sama dengan tabel.",
          },
        ],
        tips: [
          { type: "warning", text: "Pattern SN lazim: 3TB75B + 11 alfanumerik + SJ + 4 digit. Pack ID yang tidak mengikuti format ini dianggap anomali." },
          { type: "info", text: "Hanya transaksi GR (Good Receive) dengan product_id 2 (TVD) yang diperiksa. Pack ID kosong diabaikan." },
          { type: "success", text: "Tabel menampilkan total Pack ID dicek dan jumlah anomali ditemukan untuk transparansi." },
        ],
        results: [
          { label: "Total Checked", desc: "Jumlah Pack ID GR yang diperiksa" },
          { label: "Total Anomaly", desc: "Jumlah Pack ID yang tidak match pattern" },
          { label: "CSV Export", desc: "File download berisi daftar anomali" },
        ],
      },
    ],
  };
  },
  watch: {
    active(val) {
      this.isActive = val;
    },
    isActive(val) {
      this.$emit("update:active", val);
    },
  },
  methods: {
    selectTool(index) {
      this.activeTool = index;
      this.$nextTick(() => {
        const sidebar = this.$refs.panduanSidebar;
        if (!sidebar) return;
        const isHorizontal = sidebar.scrollWidth > sidebar.clientWidth;
        const refKey = "panduanNav" + index;
        const el = this.$refs[refKey];
        if (!el || !el[0]) return;
        const item = el[0];
        if (isHorizontal) {
          const scrollLeft =
            item.offsetLeft - sidebar.clientWidth / 2 + item.clientWidth / 2;
          sidebar.scrollTo({ left: scrollLeft, behavior: "smooth" });
        } else {
          const scrollTop =
            item.offsetTop - sidebar.clientHeight / 2 + item.clientHeight / 2;
          sidebar.scrollTo({ top: scrollTop, behavior: "smooth" });
        }
      });
    },
  },
};
</script>

<style>
/* Popup sizing */
.panduan-tvd-popup .vs-popup {
  width: 900px !important;
  max-width: 95vw;
  border-radius: 12px !important;
}
.panduan-tvd-popup .vs-popup--content {
  padding: 0 !important;
}
.panduan-tvd-popup .vs-popup--title h3 {
  font-size: 16px !important;
  font-weight: 700 !important;
  letter-spacing: 0.2px;
}

.panduan-container {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
}

.panduan-body {
  display: flex;
  min-height: 520px;
  max-height: 70vh;
}

/* Sidebar */
.panduan-sidebar {
  width: 230px;
  flex-shrink: 0;
  border-right: 1px solid #eee;
  overflow-y: auto;
  background: #fafafa;
  padding: 8px 0;
}
.panduan-nav-item {
  display: flex;
  align-items: center;
  padding: 10px 16px;
  cursor: pointer;
  transition: all 0.15s ease;
  border-left: 3px solid transparent;
}
.panduan-nav-item:hover {
  background: #f0f0f5;
}
.panduan-nav-active {
  background: #eef0ff !important;
  border-left-color: #6c63ff;
}
.panduan-nav-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 10px;
  font-size: 15px;
  flex-shrink: 0;
}
.panduan-nav-label {
  font-size: 13px;
  font-weight: 500;
  color: #333;
  line-height: 1.3;
}
.panduan-nav-active .panduan-nav-label {
  font-weight: 600;
  color: #6c63ff;
}

/* Content */
.panduan-content {
  flex: 1;
  overflow-y: auto;
  padding: 20px 24px;
}

.panduan-tool-header {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid #eee;
}
.panduan-tool-icon-lg {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 14px;
  font-size: 20px;
  flex-shrink: 0;
}

.panduan-section {
  margin-bottom: 20px;
}
.panduan-section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 700;
  color: #1a1a2e;
  margin-bottom: 10px;
}
.panduan-section-title i {
  font-size: 16px;
}

/* Use Case */
.panduan-usecase {
  background: #f8f9ff;
  border-radius: 8px;
  padding: 12px 16px;
  border-left: 3px solid #6c63ff;
}

/* Steps */
.panduan-steps {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.panduan-step {
  display: flex;
  gap: 12px;
}
.panduan-step-number {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  flex-shrink: 0;
  margin-top: 2px;
}
.panduan-step-body {
  flex: 1;
}
.panduan-step-title {
  font-size: 13px;
  font-weight: 700;
  color: #333;
  margin-bottom: 4px;
}
.panduan-step-desc {
  font-size: 12.5px;
  color: #555;
  line-height: 1.5;
}
.panduan-step-desc code {
  background: #f0f0f0;
  padding: 1px 5px;
  border-radius: 3px;
  font-size: 12px;
  color: #d63031;
}

/* Fields Table */
.panduan-fields-table {
  margin-top: 8px;
  border: 1px solid #eee;
  border-radius: 6px;
  overflow: hidden;
}
.panduan-fields-table table {
  width: 100%;
  border-collapse: collapse;
}
.panduan-fields-table th {
  background: #f5f5f5;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  text-align: left;
  color: #555;
}
.panduan-fields-table td {
  padding: 6px 12px;
  font-size: 12px;
  color: #444;
  border-top: 1px solid #eee;
}
.panduan-fields-table td code {
  background: #fff3e0;
  padding: 1px 5px;
  border-radius: 3px;
  font-size: 11.5px;
  color: #e17055;
}

/* Tips */
.panduan-tips {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.panduan-tip-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 8px 12px;
  border-radius: 8px;
  background: #fafafa;
}
.panduan-tip-icon {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  flex-shrink: 0;
  margin-top: 1px;
}
.panduan-tip-icon.warning {
  background: #fff3cd;
  color: #ffc107;
}
.panduan-tip-icon.info {
  background: #d1ecf1;
  color: #17a2b8;
}
.panduan-tip-icon.success {
  background: #d4edda;
  color: #28a745;
}
.panduan-tip-text {
  font-size: 12.5px;
  color: #444;
  line-height: 1.4;
}

/* Results */
.panduan-results {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.panduan-result-chip {
  display: flex;
  flex-direction: column;
  padding: 8px 14px;
  border-radius: 8px;
  border: 1px solid;
  min-width: 160px;
}

/* Scrollbar */
.panduan-content::-webkit-scrollbar,
.panduan-sidebar::-webkit-scrollbar {
  width: 6px;
}
.panduan-content::-webkit-scrollbar-track,
.panduan-sidebar::-webkit-scrollbar-track {
  background: transparent;
}
.panduan-content::-webkit-scrollbar-thumb,
.panduan-sidebar::-webkit-scrollbar-thumb {
  background: #ccc;
  border-radius: 3px;
}

/* Mobile: panduan popup & layout */
@media (max-width: 768px) {
  .panduan-tvd-popup .vs-popup {
    width: 100vw !important;
    height: 100vh !important;
    max-width: 100vw !important;
    max-height: 100vh !important;
    border-radius: 0 !important;
    margin: 0 !important;
    top: 0 !important;
    left: 0 !important;
  }
  .panduan-tvd-popup .vs-popup--header {
    padding: 12px 16px !important;
    min-height: 44px !important;
    box-sizing: border-box !important;
  }
  .panduan-tvd-popup .vs-popup--title h3 {
    font-size: 14px !important;
  }
  .panduan-tvd-popup .vs-popup--content {
    padding: 0 !important;
    height: calc(100vh - 44px) !important;
    max-height: calc(100vh - 44px) !important;
    overflow: hidden !important;
  }
  .panduan-container {
    height: 100% !important;
  }
  .panduan-body {
    display: flex !important;
    flex-direction: column !important;
    height: 100% !important;
    max-height: 100% !important;
    min-height: 0 !important;
  }
  .panduan-sidebar {
    width: 100% !important;
    border-right: none !important;
    border-bottom: 1px solid #eee !important;
    overflow-x: auto !important;
    overflow-y: hidden !important;
    -webkit-overflow-scrolling: touch;
    display: flex !important;
    flex-wrap: nowrap !important;
    padding: 6px !important;
    gap: 6px !important;
    flex-shrink: 0 !important;
    background: #fafafa !important;
  }
  .panduan-sidebar::-webkit-scrollbar {
    height: 0 !important;
    display: none !important;
  }
  .panduan-nav-item {
    border-left: none !important;
    border-bottom: 3px solid transparent !important;
    padding: 7px 12px !important;
    min-width: fit-content !important;
    white-space: nowrap !important;
    border-radius: 6px !important;
    display: inline-flex !important;
    align-items: center !important;
    flex-shrink: 0 !important;
  }
  .panduan-nav-item:hover {
    background: transparent !important;
  }
  .panduan-nav-active {
    border-left-color: transparent !important;
    border-bottom-color: #6c63ff !important;
    background: #eef0ff !important;
  }
  .panduan-nav-icon {
    margin-right: 6px !important;
    width: 24px !important;
    height: 24px !important;
    font-size: 13px !important;
    border-radius: 6px !important;
    flex-shrink: 0 !important;
  }
  .panduan-nav-label {
    font-size: 11px !important;
    font-weight: 500 !important;
    color: #333 !important;
    line-height: 1.2 !important;
  }
  .panduan-nav-active .panduan-nav-label {
    font-weight: 600 !important;
    color: #6c63ff !important;
  }
  .panduan-content {
    flex: 1 !important;
    min-height: 0 !important;
    padding: 14px 12px !important;
    overflow-y: auto !important;
    overflow-x: hidden !important;
    -webkit-overflow-scrolling: touch;
  }
  .panduan-result-chip {
    min-width: auto !important;
    flex: 1 1 calc(50% - 8px) !important;
  }
  .panduan-tool-header {
    margin-bottom: 14px !important;
    padding-bottom: 12px !important;
  }
  .panduan-tool-header h3 {
    font-size: 14px !important;
  }
  .panduan-tool-header p {
    font-size: 12px !important;
  }
  .panduan-section {
    margin-bottom: 16px !important;
  }
  .panduan-section-title {
    font-size: 13px !important;
  }
  .panduan-step-desc {
    font-size: 12px !important;
  }
  .panduan-tip-text {
    font-size: 12px !important;
  }
}
</style>
